import React from 'react';
import { Flex, Text, Button, Image, Heading } from 'rebass';
import { EMPLOYER, RECRUITER } from 'constants/index';
import styled from 'styled-components';
import filledBookmarkIcon from 'assets/icons/filledBookmark.png';
import candidate from 'assets/icons/candidate.png';
import addCandidate from 'assets/icons/addCandidate.png';
import infoIcon from 'assets/icons/info.png';
import shareIcon from 'assets/icons/share.png';
import archiveIcon from 'assets/icons/archive.png';
import Link from 'components/Link';
import colors from 'theme/colors';
import { format } from 'date-fns';

// Hooks
import useModal from 'hooks/useModal';
import useGetShareLink from 'components/Modal/ShareLinkModal/hooks/useGetShareLink';
import useJobBoardNavigation from '../hooks/useJobBoardNavigation';

const Wrapper = styled(Flex)`
  border-bottom: 4px solid ${colors.gray};
`;

const HeadingButton = styled(Button)`
  cursor: pointer;
  outline: none;
  user-select: none;
`;

const JobBoardNavigation: React.FC<any> = ({ userRole, userJobPost, companyName }): JSX.Element => {
  const { id, title, type, createdAt } = userJobPost;
  const { getShareLinkHandler } = useGetShareLink(userJobPost.id);
  const { openModalHandler } = useModal();
  const {
    restoreCandidateHandler,
    viewCandidateDetailsHandler,
    candidateCount,
  } = useJobBoardNavigation();

  //  todo: DenisKozyrev - the component is too big, it's better to split it into several smaller components
  return (
    <Flex>
      {userRole === EMPLOYER && (
        <Wrapper width="100%" flexDirection="column" pb={5}>
          <Link to="/job-posts" color={colors.lightGreen} hover={colors.lightGreen} mb={5}>
            <Flex>
              <Text fontSize="18px">{'< Back to '}</Text>
              <Text fontSize="18px" fontWeight={6}>
                Postings
              </Text>
            </Flex>
          </Link>
          <Heading fontSize="36px" mb={5}>
            {companyName ? `${title} - ${companyName}` : title}
          </Heading>
          <Flex width="100%" justifyContent="space-between" mb={6} css="max-width: 900px;">
            <Flex>
              <Heading as="h3" fontSize={['24px']} fontWeight={6} mr={1}>
                Posted on:
              </Heading>
              <Text as="p" fontSize={['24px']}>
                {format(new Date(createdAt), 'MMM DD, YYYY')}
              </Text>
            </Flex>
            <Flex>
              <Heading as="h3" fontSize={['24px']} fontWeight={6} mr={1}>
                Job ID:
              </Heading>
              <Text as="p" fontSize={['24px']}>
                {id}
              </Text>
            </Flex>
            <Flex>
              <Heading as="h3" fontSize={['24px']} fontWeight={6} mr={1}>
                Position Type:
              </Heading>
              <Text as="p" fontSize={['24px']}>
                {type}
              </Text>
            </Flex>
          </Flex>
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            css="max-width: 700px;"
          >
            <Flex alignItems="center">
              <Image src={candidate} mr={2} />
              <Text fontSize="24px">{candidateCount || '0'}</Text>
            </Flex>
            <Link to={`/job-post-details/${id}`}>
              <Flex alignItems="center">
                <Image src={infoIcon} mr={2} />
                <Text fontSize="18px" fontWeight={6} color="lightGreen">
                  View details
                </Text>
              </Flex>
            </Link>
            <HeadingButton
              fontSize="24px"
              fontWeight={6}
              color="lightGreen"
              bg="transparent"
              px={0}
              py={0}
              onClick={openModalHandler({
                title: 'archivedCandidates',
                data: { onRestore: restoreCandidateHandler, onInfo: viewCandidateDetailsHandler },
              })}
            >
              <Flex alignItems="center">
                <Image src={archiveIcon} mr={2} />
                <Text fontSize="18px" fontWeight={6} color="lightGreen">
                  Archived Candidates
                </Text>
              </Flex>
            </HeadingButton>
          </Flex>
        </Wrapper>
      )}
      {userRole === RECRUITER && (
        <Wrapper width="100%" flexDirection="column" pb={5}>
          <Flex width="100%" justifyContent="space-between" alignItems="center" mb={5}>
            <Link to="/job-posts" color={colors.lightGreen} hover={colors.lightGreen}>
              <Flex>
                <Text fontSize="18px">{'< Back to '}</Text>
                <Text fontSize="18px" fontWeight={6}>
                  Your Jobs
                </Text>
              </Flex>
            </Link>
            <Flex>
              <Image src={filledBookmarkIcon} mr={2} />
              <Text fontSize="18px" fontWeight={6} color="lightGreen">
                Saved
              </Text>
            </Flex>
          </Flex>
          <Heading fontSize="36px" mb={5}>
            {title} - {companyName}
          </Heading>
          <Flex width="100%" justifyContent="space-between" mb={6} css="max-width: 820px;">
            <Flex>
              <Heading as="h3" fontSize={['16px']} mr={1}>
                Posted on:
              </Heading>
              <Text as="p">{format(new Date(createdAt), 'MMM DD, YYYY')}</Text>
            </Flex>
            <Flex>
              <Heading as="h3" fontSize={['16px']} mr={1}>
                Job ID:
              </Heading>
              <Text as="p">{id}</Text>
            </Flex>
            <Flex>
              <Heading as="h3" fontSize={['16px']} mr={1}>
                Position Type:
              </Heading>
              <Text as="p">{type}</Text>
            </Flex>
          </Flex>
          <Flex width="100%" justifyContent="space-between" alignItems="center">
            <Link to={`/job-post-details/${id}`}>
              <Flex alignItems="center">
                <Image src={infoIcon} mr={2} />
                <Text fontSize="18px" fontWeight={6} color="lightGreen">
                  View details
                </Text>
              </Flex>
            </Link>
            <Flex width="100%" justifyContent="space-between" css="max-width: 520px;">
              <Flex alignItems="center">
                <Image src={candidate} mr={2} />
                <Text fontSize="24px">{candidateCount || '0'}/5</Text>
              </Flex>
              <Link to={`/add-candidate/${id}`}>
                <Flex alignItems="center">
                  <Image src={addCandidate} mr={2} />
                  <Text fontSize="18px" fontWeight={6} color="lightGreen">
                    Add a candidate
                  </Text>
                </Flex>
              </Link>
              <HeadingButton
                fontSize="24px"
                fontWeight={6}
                color="lightGreen"
                bg="transparent"
                px={0}
                py={0}
                onClick={getShareLinkHandler}
              >
                <Flex alignItems="center">
                  <Image src={shareIcon} mr={2} />
                  <Text fontSize="18px" fontWeight={6} color="lightGreen">
                    Share a link
                  </Text>
                </Flex>
              </HeadingButton>
            </Flex>
          </Flex>
        </Wrapper>
      )}
    </Flex>
  );
};

export default JobBoardNavigation;
